import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaBuilding,
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaTimes,
} from 'react-icons/fa';
import { crmCompanyApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import StateSelect from '../../components/StateSelect';
import './CRM.css';
import './CRM_COMPANIES.css';

const fallbackCompanies = [
  { id: 1, name: 'Mehta Oxygen Pvt. Ltd.', industry: 'Industrial Gas', city: 'Ahmedabad', state: 'Gujarat', country: 'India', phone: '9876543210', email: 'info@mehtaoxygen.in', website: 'mehtaoxygen.in', gstin: '24AABCM1234F1Z5', leadCount: 3, opportunityCount: 2, owner: 'Rahul Sharma', status: 'Active' },
  { id: 2, name: 'Kumar Industrial Gases', industry: 'Manufacturing', city: 'Delhi', state: 'Delhi', country: 'India', phone: '9811122233', email: 'contact@kumarindgas.com', website: '', gstin: '07AACCK5678G1Z3', leadCount: 2, opportunityCount: 1, owner: 'Priya Verma', status: 'Active' },
  { id: 3, name: 'Devi Gas Agency', industry: 'Distribution', city: 'Jaipur', state: 'Rajasthan', country: 'India', phone: '9765544332', email: 'sunita@devigas.in', website: 'devigas.in', gstin: '08AAECD9012H1Z8', leadCount: 1, opportunityCount: 1, owner: 'Amit Kumar', status: 'Active' },
  { id: 4, name: 'Ali Welding Works', industry: 'Services', city: 'Mumbai', state: 'Maharashtra', country: 'India', phone: '9898989898', email: 'ali@aliwelding.com', website: '', gstin: '27AAGFA3456J1Z1', leadCount: 1, opportunityCount: 0, owner: 'Rahul Sharma', status: 'Active' },
  { id: 5, name: 'Sharma Medical Gas', industry: 'Healthcare', city: 'Lucknow', state: 'Uttar Pradesh', country: 'India', phone: '9777666555', email: 'pooja@sharmamedical.in', website: '', gstin: '09AAECS7890K1Z6', leadCount: 2, opportunityCount: 1, owner: 'Sneha Patel', status: 'Active' },
];

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState(fallbackCompanies);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | company object
  const [form, setForm] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmCompanyApi.getAll({ size: 200 });
      if (Array.isArray(res.data)) setCompanies(res.data);
      else if (res.data?.content) setCompanies(res.data.content);
    } catch { /* fallback */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setForm({ name: '', industry: '', city: '', state: '', country: 'India', phone: '', email: '', website: '', gstin: '', owner: '', status: 'Active' }); setModal('new'); };
  const openEdit = (c) => { setForm({ ...c }); setModal(c); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Company name is required.'); return; }
    try {
      if (modal === 'new') {
        const res = await crmCompanyApi.create(form);
        setCompanies((p) => [...p, res.data || { ...form, id: Date.now() }]);
        toast.success('Company created.');
      } else {
        await crmCompanyApi.update(modal.id, form);
        setCompanies((p) => p.map((c) => c.id === modal.id ? { ...c, ...form } : c));
        toast.success('Company updated.');
      }
    } catch { toast.error('Failed to save company.'); }
    setModal(null);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"?`)) return;
    try { await crmCompanyApi.delete(c.id); setCompanies((p) => p.filter((x) => x.id !== c.id)); toast.success('Company deleted.'); }
    catch { toast.error('Failed to delete company.'); }
  };

  const filtered = companies.filter((c) => {
    if (industryFilter && c.industry !== industryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.name || '').toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
    }
    return true;
  });

  const industries = [...new Set(companies.map((c) => c.industry).filter(Boolean))];
  const fields = [
    { k: 'name', label: 'Company Name', req: true }, { k: 'industry', label: 'Industry' },
    { k: 'city', label: 'City' }, { k: 'state', label: 'State', type: 'state' }, { k: 'country', label: 'Country' },
    { k: 'phone', label: 'Phone' }, { k: 'email', label: 'Email', type: 'email' }, { k: 'website', label: 'Website' },
    { k: 'gstin', label: 'GSTIN' }, { k: 'owner', label: 'Owner' },
  ];

  return (
    <div className="crm-page crm-companies-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon" aria-hidden="true"><FaBuilding /></div>
          <div>
            <h1 className="crm-title">Companies</h1>
            <p className="crm-subtitle">Company master — accounts linked to leads & opportunities</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button type="button" className="crm-btn crm-btn-primary" onClick={openCreate}>
            <FaPlus aria-hidden="true" /> New Company
          </button>
        </div>
      </div>

      <div className="crm-filters">
        <div className="crm-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search companies"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, city, phone..."
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter companies by industry">
          <AppDropdown
            value={industryFilter}
            options={industries.map((i) => ({ value: i, label: i }))}
            onChange={setIndustryFilter}
            placeholder="All Industries"
            menuWidth={220}
            menuClassName="crm-dropdown-window"
          />
        </div>
        <button type="button" className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => { setSearch(''); setIndustryFilter(''); }}>Clear</button>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Company</th><th>Industry</th><th>City</th><th>Phone</th><th>GSTIN</th><th>Leads</th><th>Opportunities</th><th>Owner</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 9 }).map((__, cc) => (
                    <td key={cc}><SkeletonBlock width={cc === 0 ? 130 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td className="link-cell" onClick={() => navigate(`/crm/companies/${c.id}`)}>
                  <strong>{c.name}</strong>
                  {c.website && <div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{c.website}</div>}
                </td>
                <td>{c.industry || '—'}</td>
                <td>{c.city}{c.state ? `, ${c.state}` : ''}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{c.phone || '—'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{c.gstin || '—'}</td>
                <td>{c.leadCount ?? 0}</td>
                <td>{c.opportunityCount ?? 0}</td>
                <td>{c.owner || '—'}</td>
                <td>
                  <div className="crm-actions">
                    <button type="button" className="crm-icon-btn view" title="View" aria-label={`View ${c.name}`} onClick={() => navigate(`/crm/companies/${c.id}`)}><FaEye aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn" title="Edit" aria-label={`Edit ${c.name}`} onClick={() => openEdit(c)}><FaEdit aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn lost" title="Delete" aria-label={`Delete ${c.name}`} onClick={() => remove(c)}><FaTrash aria-hidden="true" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={9}>
                <div className="crm-empty">
                  <div className="crm-empty-icon" aria-hidden="true"><FaBuilding /></div>
                  <div className="crm-empty-title">No companies found</div>
                  <div className="crm-empty-desc">Create your first company to group leads and contacts.</div>
                </div>
              </td></tr>
            )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {modal && createPortal(
        <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="crm-modal crm-modal-lg" role="dialog" aria-modal="true">
            <div className="crm-modal-header">
              <h3>{modal === 'new' ? 'New Company' : 'Edit Company'}</h3>
              <button type="button" className="crm-modal-close" onClick={() => setModal(null)} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
            </div>
            <div className="crm-modal-body">
              <div className="crm-form-grid">
                {fields.map((f) => (
                  <div className={`crm-field ${f.k === 'name' ? 'crm-field-full' : ''}`} key={f.k}>
                    <label>{f.label} {f.req && <span className="req">*</span>}</label>
                    {f.type === 'state' ? (
                      <StateSelect value={form[f.k] || ''} onChange={(v) => setForm((p) => ({ ...p, [f.k]: v }))} />
                    ) : (
                      <input type={f.type || 'text'} value={form[f.k] || ''} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="crm-modal-footer">
              <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button type="button" className="crm-btn crm-btn-primary" onClick={save}>{modal === 'new' ? 'Create' : 'Save'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
